<script lang="ts" setup>
const props = defineProps<{ data: Business }>()

useSeoMeta({
  title: props.data?.name,
  twitterDescription: props.data?.brief ?? '',
  twitterTitle: props.data?.name ?? `We're on ${name}`,
  ogTitle: props.data?.name ?? `We're on ${name}`,
  ogDescription: props.data?.brief ?? '',
  description: props.data?.brief ?? '',
})

const schema = computed(() => buildBusinessSchema(props.data!))

// Precompute the JSON string (SSR safe)
const schemaJson = computed(() => JSON.stringify(schema.value))

useHead({
  link: [
    {
      rel: 'canonical',
      href: getPublicRoute('business', props.data, true),
    },
  ],
  script: [
    {
      type: 'application/ld+json',
      innerHTML: schemaJson.value,
    },
  ],
})

defineOgImageComponent('Business', {
  title: props.data?.name ?? `We're on ${name}`,
  backgroundColor: 'bg-slate-100',
  image: props.data?.mainImage,
})

const { isOpenNow } = useBusinessHours(props.data?.hoursFull)

function scrollToReviews() {
  const reviews = document.getElementById('reviews')
  if (reviews) {
    reviews.scrollIntoView({ behavior: 'smooth' })
  }
}

defineOgImageComponent('Business', { title: props.data.name, description: props.data.brief, image: getImagePath({ collectionId: BUSINESS_COLLECTION_ID, id: props.data.id, src: props.data?.coverImage ?? props.data?.gallery?.[0] ?? '' }) })

const gallery = ref()

const { pause: pauseSlideshow, resume: playSlideshow, isActive: slideshowIsPlaying } = useIntervalFn(() => {
  if (!gallery.value) return

  if (gallery.value.page === gallery.value.pages) {
    return gallery.value.select(0)
  }

  gallery.value.next()
}, 3000)

const classBusiness = COLLECTION_CLASS_MAP.businesses
</script>

<template>
  <div v-if="data">
    <div
      v-if="data?.id && (data?.coverImage || data?.gallery?.length)"
      class="w-full max-w-screen-2xl px-6 mx-auto pt-24 overflow-hidden"
    >
      <div class="w-full rounded-lg overflow-clip  relative max-h-[1200px]  ">
        <CollectionBusinessSocialIcons
          :business="data"
          class="absolute top-3 right-3 z-10"
        />
        <ImageFull
          :id="data.id"
          :key="data.id"
          :src="data.coverImage ?? data.gallery?.[0]"
          :collection-id="BUSINESS_COLLECTION_ID"
          class="h-auto w-full object-cover"
        />
      </div>
    </div>
    <div class="w-full max-w-screen-2xl mx-auto py-32 px-6">
      <div class="flex flex-col max-w-6xl ">
        <CollectionBusinessOnlineIndicator
          size="2xl"
          :visible="isOpenNow"
        >
          <HeadingText
            tag="h1"
            xl
            class="text-grad-pink"
          >
            {{ data.name }}
          </HeadingText>
        </CollectionBusinessOnlineIndicator>
        <section
          v-if="data?.averageRating"
          class="flex gap-3 items-center text-yellow pt-6 text-2xl md:text-3xl xl:text-5xl font-display font-bold"
        >
          <Stars
            :stars="data.averageRating"
            size="2xl"
            class="cursor-pointer"
            @click="scrollToReviews"
          />
          <p><span class="text-3xl md:text-4xl xl:text-7xl">{{ data.averageRating }}</span> / 5</p>
        </section>
        <p class="text-xl lg:text-2xl xl:text-3xl pt-6">
          {{ data.brief }}
        </p>
      </div>
    </div>

    <div class="w-full max-w-screen-2xl mx-auto px-6 flex gap-3 items-center mb-32 font-semibold">
      <p
        class="font-display text-xl lg:text-2xl uppercase font-black"
        :class="classBusiness.heading"
      >
        Share This Profile!
      </p>
      <SocialShare
        facebook
        twitter
        linked-in
      />
    </div>

    <div class="w-full max-w-screen-xl mx-auto py-32 px-6">
      <section
        v-if="data?.description"
        class="htmldescription"
      >
        <div
          data-allow-mismatch
          v-html="data.description"
        />
      </section>
    </div>
    <div
      v-if="data?.id && data?.gallery?.length"
      class="w-full max-w-screen-2xl px-6 mx-auto py-24"
    >
      <p class="text-3xl lg:text-4xl text-right font-display font-black text-primary uppercase pb-2 ">
        📷 {{ data.name }}'s
        Gallery
      </p>
      <UCarousel
        v-if="data?.gallery?.length"
        ref="gallery"
        :items="data.gallery"
        indicators
        :ui="{ item: 'basis-full  flex', container: ' rounded-lg h-[60vh] max-h-[1200px] overflow-hidden' }"
        arrows
      >
        <template #default="{ item }">
          <div class="w-full h-full  relative bg-slate-300 shadow-inner flex items-center justify-center">
            <ImageFull
              :id="data.id"
              :key="item"
              :src="item"
              :collection-id="BUSINESS_COLLECTION_ID"
              class="absolute inset-0 w-full h-full object-contain"
            />
          </div>
        </template>
        <template #prev>
          <div class="absolute bottom-2 right-2 flex gap-1 z-20">
            <transition
              name="fade"
              mode="out-in"
            >
              <UButton
                v-if="!slideshowIsPlaying"
                icon="heroicons:play-solid"
                aria-label="Play"
                class="hover:bg-lightblue"
                @click="playSlideshow"
              />
              <UButton
                v-else
                icon="heroicons:pause-solid"
                aria-label="Pause"
                class="hover:bg-lightblue"
                @click="pauseSlideshow"
              />
            </transition>
          </div>
        </template>
      </UCarousel>
    </div>
    <div
      v-if="data?.qualifications"
      class="w-full max-w-screen-xl mx-auto py-32 px-6"
    >
      <p class="text-3xl lg:text-4xl text-right font-display font-black text-primary uppercase pb-2 ">
        Qualifications & Experience
      </p>
      <section
        v-if="data?.qualifications"
        class="htmldescription"
      >
        <div
          data-allow-mismatch
          v-html="data.qualifications"
        />
      </section>
    </div>
    <CollectionOpeningHoursDisplay
      :data="data.hoursFull ?? data.expand?.openingHours ?? []"
      class="mb-24"
    />
    <div id="reviews">
      <CollectionReviewAll
        v-if="data.id"
        :record-id="data.id"
        :record-name="data.name"
        collection="business"
        :average-rating="data.averageRating"
        class="bg-slate-200 pb-32"
      />
    </div>
    <div class="bg-blue text-white">
      <div class="w-full flex flex-wrap   max-w-screen-2xl mx-auto py-32 px-6 text-white gap-6">
        <div class="gap-3 w-full lg:flex-1 flex flex-col">
          <CollectionBusinessOnlineIndicator
            :visible="isOpenNow"
            size="lg"
          >
            <HeadingText
              tag="h3"
              class=" text-yellow inline-flex items-center"
            >
              Get In Touch
              <UBadge
                v-if="isOpenNow"
                size="lg"
                class="ml-3 text-lg"
                color="green"
              >
                Open
              </UBadge>
            </HeadingText>
          </CollectionBusinessOnlineIndicator>
          <hr class="my-3 opacity-40">

          <CollectionBusinessSocials
            :business="data"
            class="xl:mx-auto w-full"
            button-color="pinkblue"
          />
        </div>

        <div
          v-if="data.map?.name"
          class="w-full lg:flex-1 gap-3 flex flex-col"
        >
          <HeadingText
            tag="h3"
            class="text-right text-yellow"
          >
            Find Us
          </HeadingText>
          <hr class="my-3 opacity-40">
          <a
            href="#map"
            class="hover:text-primary-200 w-full text-right"
          >{{ data.map.name }}</a>
        </div>
      </div>
    </div>
    <LocationMap
      v-if="data.map?.lat && data.map.lng"
      :zoom="15"
      :items="[data]"
      class="w-full"
    />
    <div class="mx-auto flex items-center justify-center pt-12 lg:pt-16 bg-gray-200">
      <ImageFull
        v-if="data?.id && data?.logo"
        :id="data.id"
        :src="data.logo"
        loading="lazy"
        :collection-id="BUSINESS_COLLECTION_ID"
        class=" w-auto h-32 lg:h-40 xl:h-64 object-contain mb-12"
      />
    </div>
  </div>
</template>

<style lang="postcss" scoped></style>
